import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileSpreadsheet, RefreshCw, Search, Trash2 } from 'lucide-react';

const Sparkline = ({ trajectory, color = '#64748b' }) => {
  if (!trajectory?.length) return <span className="text-slate-300 text-xs">—</span>;
  const points = trajectory.filter((_, i) => i % Math.max(1, Math.floor(trajectory.length / 20)) === 0);
  if (points.length < 2) return <span className="text-slate-300 text-xs">—</span>;
  const weights = points.map(p => p.weight || 0);
  const min = Math.min(...weights);
  const max = Math.max(...weights);
  const range = Math.max(max - min, 1);
  const w = 60;
  const h = 16;
  const path = points.map((p, i) => {
    const x = (i / (points.length - 1)) * w;
    const y = h - ((p.weight - min) / range) * h;
    return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(' ');
  return (
    <svg width={w} height={h} className="inline-block">
      <path d={path} stroke={color} strokeWidth="1.5" fill="none" />
    </svg>
  );
};

const actionabilityChip = (a) => {
  if (!a) return null;
  const styles = {
    ACTIONABLE: 'bg-emerald-100 text-emerald-700',
    VALIDATION_REQUIRED: 'bg-amber-100 text-amber-700',
    MONITOR_ONLY: 'bg-slate-200 text-slate-600',
  };
  const label = { ACTIONABLE: 'Actionable', VALIDATION_REQUIRED: 'Validate', MONITOR_ONLY: 'Monitor' };
  return (
    <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded ${styles[a] || 'bg-slate-100 text-slate-500'}`}>
      {label[a] || a}
    </span>
  );
};

export default function RecordSelectorEnhanced({
  savedRecords,
  loading,
  selectedRecordIds,
  onToggleSelection,
  onConfirmDelete,
  onNavigateToUploader,
  isDemo,
  profileColors,
  savedViews = [],
  onSaveView,
  onDeleteView,
  onApplyView,
  onLoadSelected,
  onClearSelection,
}) {
  const [query, setQuery] = useState('');
  const [speciesFilter, setSpeciesFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [viewName, setViewName] = useState('');

  const species = useMemo(
    () => Array.from(new Set(savedRecords.map(r => r.species).filter(Boolean))).sort(),
    [savedRecords]
  );

  const filteredRecords = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = savedRecords.filter(r => {
      if (q && !(`${r.name} ${r.species}`).toLowerCase().includes(q)) return false;
      if (speciesFilter !== 'all' && r.species !== speciesFilter) return false;
      const isClosed = r.name?.toLowerCase().includes('closed');
      if (statusFilter === 'open' && isClosed) return false;
      if (statusFilter === 'closed' && !isClosed) return false;
      return true;
    });
    list = [...list].sort((a, b) => {
      if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
      if (sortBy === 'days') return (b.days_in_water || 0) - (a.days_in_water || 0);
      if (sortBy === 'fish') return (b.input_number || 0) - (a.input_number || 0);
      if (sortBy === 'weight') return (b.input_average_weight || 0) - (a.input_average_weight || 0);
      return 0;
    });
    return list;
  }, [savedRecords, query, speciesFilter, statusFilter, sortBy]);

  const handleSave = () => {
    if (!viewName.trim()) return;
    onSaveView?.({ name: viewName.trim(), recordIds: selectedRecordIds });
    setViewName('');
  };

  return (
    <Card className="mb-6 border-[#0891b2]/30" data-testid="record-selector">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base text-[#0891b2] flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Select Saved Uploads to Compare
          </CardTitle>
          <span className="text-xs text-gray-500" data-testid="selected-count">{selectedRecordIds.length}/10 selected</span>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-gray-500">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading saved records...
          </div>
        ) : savedRecords.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FileSpreadsheet className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p>No saved spreadsheet uploads found.</p>
            <Button variant="link" onClick={onNavigateToUploader} className="text-[#0891b2] mt-2">
              Go to Spreadsheet Uploader
            </Button>
          </div>
        ) : (
          <>
            {/* Saved views row */}
            {savedViews?.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="saved-views-row">
                <span className="text-[11px] text-slate-500 mr-1">Saved views:</span>
                {savedViews.map(v => (
                  <div key={v.id} className="inline-flex items-center gap-1 rounded-full bg-slate-100 border border-slate-200 pl-2 pr-0.5 py-0.5 text-[11px]">
                    <button
                      className="hover:text-sky-700"
                      onClick={() => onApplyView?.(v)}
                      data-testid={`apply-view-${v.id}`}
                    >
                      {v.name} <span className="text-slate-400">({v.recordIds?.length || 0})</span>
                    </button>
                    <button
                      className="text-slate-400 hover:text-rose-600 h-4 w-4 inline-flex items-center justify-center rounded-full"
                      onClick={() => onDeleteView?.(v.id)}
                      title="Delete view"
                      data-testid={`delete-view-${v.id}`}
                    >×</button>
                  </div>
                ))}
              </div>
            )}

            {/* Filter / sort controls */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
              <div className="relative md:col-span-2">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search by name or species..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="pl-8 h-9 text-sm"
                  data-testid="record-search-input"
                />
              </div>
              <Select value={speciesFilter} onValueChange={setSpeciesFilter}>
                <SelectTrigger className="h-9 text-sm" data-testid="species-filter-select">
                  <SelectValue placeholder="Species" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All species</SelectItem>
                  {species.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="grid grid-cols-2 gap-2">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="h-9 text-sm" data-testid="status-filter-select">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="h-9 text-sm" data-testid="sort-by-select">
                    <SelectValue placeholder="Sort" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name">Name</SelectItem>
                    <SelectItem value="days">Days (desc)</SelectItem>
                    <SelectItem value="fish">Fish count (desc)</SelectItem>
                    <SelectItem value="weight">Weight (desc)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Record table */}
            <div className="max-h-[400px] overflow-y-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-100">
                  <tr className="border-b">
                    <th className="py-2 px-3 text-left w-10"></th>
                    <th className="py-2 px-3 text-left text-xs text-gray-600">Name</th>
                    <th className="py-2 px-3 text-left text-xs text-gray-600">Species</th>
                    <th className="py-2 px-3 text-left text-xs text-gray-600">Curve</th>
                    <th className="py-2 px-3 text-left text-xs text-gray-600">Input Wt</th>
                    <th className="py-2 px-3 text-left text-xs text-gray-600">Fish</th>
                    <th className="py-2 px-3 text-left text-xs text-gray-600">Days</th>
                    <th className="py-2 px-3 text-left text-xs text-gray-600">Status</th>
                    {!isDemo && <th className="py-2 px-3 text-center text-xs text-gray-600 w-10"></th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map((record) => {
                    const isSelected = selectedRecordIds.includes(record.id);
                    const colorIdx = selectedRecordIds.indexOf(record.id);
                    const color = isSelected && colorIdx >= 0 ? profileColors[colorIdx % profileColors.length] : null;
                    const isClosed = record.name?.toLowerCase().includes('closed');
                    return (
                      <tr
                        key={record.id}
                        className={`border-b cursor-pointer hover:bg-slate-50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`}
                        onClick={() => onToggleSelection(record.id)}
                        data-testid={`record-row-${record.id}`}
                      >
                        <td className="py-2 px-3">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => onToggleSelection(record.id)}
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`record-checkbox-${record.id}`}
                            />
                            {isSelected && color && (
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color.stroke }} />
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-3 font-medium">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span>{record.name}</span>
                            {actionabilityChip(record.latest_actionability)}
                          </div>
                        </td>
                        <td className="py-2 px-3 text-gray-600">{record.species}</td>
                        <td className="py-2 px-3">
                          <Sparkline trajectory={record.actual_trajectory} color={color?.stroke || '#94a3b8'} />
                        </td>
                        <td className="py-2 px-3">{record.input_average_weight != null ? `${Number(record.input_average_weight).toFixed(1)}g` : '—'}</td>
                        <td className="py-2 px-3">{record.input_number?.toLocaleString()}</td>
                        <td className="py-2 px-3">{record.days_in_water || '-'}</td>
                        <td className="py-2 px-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${isClosed ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-700'}`}>
                            {isClosed ? 'Closed' : 'Open'}
                          </span>
                        </td>
                        {!isDemo && (
                          <td className="py-2 px-3 text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                              onClick={(e) => onConfirmDelete(e, record.id)}
                              title="Delete record (permanent)"
                              data-testid={`delete-record-${record.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                  {filteredRecords.length === 0 && (
                    <tr><td colSpan={9} className="text-center py-6 text-slate-400 text-sm">No records match the current filter.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Load + save-view controls */}
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <Button
                size="sm"
                onClick={onLoadSelected}
                disabled={selectedRecordIds.length < 2}
                className="bg-[#0891b2] hover:bg-[#0e7490] text-white"
                data-testid="load-comparison-btn"
              >
                Compare {selectedRecordIds.length || ''} profile{selectedRecordIds.length === 1 ? '' : 's'}
              </Button>
              <Button size="sm" variant="outline" onClick={onClearSelection} data-testid="clear-selection-btn">
                Clear
              </Button>
              {selectedRecordIds.length >= 2 && (
                <div className="flex items-center gap-1 ml-auto">
                  <Input
                    placeholder="Name this view…"
                    value={viewName}
                    onChange={(e) => setViewName(e.target.value)}
                    className="h-8 w-40 text-xs"
                    data-testid="save-view-input"
                  />
                  <Button size="sm" variant="outline" onClick={handleSave} disabled={!viewName.trim()} data-testid="save-view-btn">
                    Save view
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
